import React, { useCallback, useEffect, useState } from 'react';
import { IonSpinner, IonToast } from '@ionic/react';
import { ServiceEstimate, ServiceSettingsBundle } from '../../../interfaces/ServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import EstimateEditor from './EstimateEditor';
import OfficeQueue from './OfficeQueue';
import ProposalView from './ProposalView';
import SavedEstimates from './SavedEstimates';
import ServiceSettingsAdmin from './ServiceSettingsAdmin';
import './ServiceEstimates.css';

type Tab = 'estimate' | 'proposal' | 'saved' | 'office';

/**
 * Service estimates (Field Estimator), laid out like the Alpha Insights 2.0 prototype:
 * Estimate · Proposal · Saved · Office. The tech submits an estimate; Admin or Director assigns the ACS#
 * on the Office tab (and changes settings there); the customer signs on the Proposal tab.
 * Mount on a page inside the AE2 shell and add a "Service Estimates" menu item in RoutesList.tsx,
 * wrapped in CheckRole for the Service, Admin and Director roles.
 */
const ServiceEstimates: React.FC = () => {
  const [tab, setTab] = useState<Tab>('estimate');
  const [bundle, setBundle] = useState<ServiceSettingsBundle | null>(null);
  const [current, setCurrent] = useState<ServiceEstimate | null>(null);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [queueCount, setQueueCount] = useState(0);

  const loadSettings = useCallback(() => {
    serviceApi.settings().then(setBundle).catch((e) => setError(e instanceof Error ? e.message : 'Could not load Service estimates.'));
  }, []);
  useEffect(loadSettings, [loadSettings]);
  useEffect(() => {
    serviceApi.officeQueue().then((q) => setQueueCount(q.length)).catch(() => setQueueCount(0));
  }, [refreshKey]);

  const saved = useCallback((e: ServiceEstimate) => { setCurrent(e); setRefreshKey((k) => k + 1); }, []);
  /** An estimate changed elsewhere (Office tab): refresh the open one if it's the same. */
  const changed = useCallback((e: ServiceEstimate) => {
    setCurrent((cur) => (cur && cur.id === e.id ? e : cur));
    setRefreshKey((k) => k + 1);
  }, []);
  /** A draft was deleted from the Saved tab: close it in the editor if it was the open one. */
  const deleted = useCallback((id: string) => {
    setCurrent((cur) => (cur && cur.id === id ? null : cur));
    setRefreshKey((k) => k + 1);
  }, []);
  const open = (e: ServiceEstimate) => {
    if (dirty && !window.confirm('Discard the unsaved changes to the open estimate?')) return;
    setCurrent(e);
    setTab('estimate');
  };

  const tabs: [Tab, string][] = [
    ['estimate', 'Estimate'],
    ['proposal', 'Proposal'],
    ['saved', 'Saved'],
    ['office', queueCount ? `Office (${queueCount})` : 'Office'],
  ];

  if (error) return <div className="service-estimates"><p className="service-estimates-error">{error}</p></div>;
  if (!bundle) return <div className="service-estimates"><IonSpinner /></div>;

  return (
    <div className="service-estimates">
      <header className="service-estimates-head">
        <div>
          <h1>Service Estimates</h1>
          <p>Quote mechanical service work on site, get the ACS# from the office, and have the customer sign.</p>
        </div>
        <div className="service-estimates-tabs" role="tablist">
          {tabs.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={tab === value ? 'active' : ''}
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Kept mounted so unsaved edits survive a look at the Proposal or Saved tab. */}
      <div hidden={tab !== 'estimate'}>
        <EstimateEditor
          bundle={bundle}
          estimate={current}
          onSaved={saved}
          onNew={() => { if (!dirty || window.confirm('Discard the unsaved changes?')) setCurrent(null); }}
          notify={setToast}
          onDirtyChange={setDirty}
          onOpenProposal={() => setTab('proposal')}
        />
      </div>
      {tab === 'proposal' && (
        <ProposalView bundle={bundle} estimate={current} dirty={dirty} onSaved={saved} notify={setToast} onGoEstimate={() => setTab('estimate')} />
      )}
      {tab === 'saved' && <SavedEstimates refreshKey={refreshKey} onOpen={open} onDeleted={deleted} />}
      {tab === 'office' && (
        <div className="service-estimates-office">
          <OfficeQueue canAdmin={bundle.can_admin} refreshKey={refreshKey} onOpen={open} onChanged={changed} notify={setToast} />
          {bundle.can_admin && <ServiceSettingsAdmin bundle={bundle} onChanged={loadSettings} notify={setToast} />}
        </div>
      )}

      <IonToast isOpen={!!toast} message={toast} duration={3000} onDidDismiss={() => setToast('')} position="bottom" />
    </div>
  );
};

export default ServiceEstimates;
